import React, { useEffect, useMemo, useState } from 'react'
import { Box, Typography, useMediaQuery } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Callout,
  DraftPreviewCard,
  Dropzone,
  FormGroup,
  Input,
  SegmentedOptionGroup,
  ThumbPicker,
  UploadQueue,
  type UploadQueueItem,
} from '@/design-system'
import { AlbumPrivacy } from '../../types'
import { useCreateAlbum } from '../../hooks/useAlbumDetail'
import { useAlbumUpload } from '../../hooks/useAlbumUpload'
import { useCreateAlbumLayout } from '../../layouts/CreateAlbumLayout'
import { fireAlertToast } from '@/design-system/alerts'
import { useFeatureGate, FEATURE_KEYS } from '@/context/SubscriptionContext'
import { UPLOAD_RESIZE_DIM } from '@/lib/featureKeys'

type UploadQuality = 'standard' | 'full'

const PRIVACY_OPTIONS = [
  {
    value: 'private',
    title: '🔒 Private',
    description: 'Only you, until you cut a key. The default.',
  },
  {
    value: 'unlisted',
    title: '⚿ Anyone with a key',
    description: 'Link or QR opens it. Revocable per key.',
  },
]

type StagedFile = {
  id: string
  file: File
  src: string
  progress: number
}

const Stage = styled(Box)({
  maxWidth: 1060,
  margin: '0 auto',
  padding: '44px 32px 80px',
  display: 'grid',
  gridTemplateColumns: '1fr 340px',
  gap: 54,
  alignItems: 'start',
  '@media (max-width: 940px)': {
    gridTemplateColumns: '1fr',
    gap: 36,
    padding: '28px 20px 64px',
  },
})

const Panel = styled(Box)({
  minWidth: 0,
})

const PreviewRail = styled(Box)({
  position: 'sticky',
  top: 84,
  minWidth: 0,
  '@media (max-width: 940px)': {
    position: 'static',
  },
})

const RailLabel = styled('div')({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9.5,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: 'var(--ds-ink-subtle)',
  marginBottom: 14,
})

const GoRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  flexWrap: 'wrap',
})

const Hint = styled('span')({
  fontSize: 12.5,
  color: 'var(--ds-ink-subtle)',
})

/* Compact "Upload quality" and "Downloads" controls folded into the Photos
   section header — a full second SegmentedOptionGroup card here duplicated
   the privacy card's weight/height for what are lower-stakes, changeable-
   later settings (also settable in Share afterward), so these are
   single-line pill toggles instead of their own section. */
const SectionHeaderRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: 10,
  marginBottom: 14,
})

const SectionHeaderLabel = styled('div')({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9.5,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: 'var(--ds-ink-subtle)',
})

const ToggleLabel = styled('span')({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9.5,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: 'var(--ds-ink-subtle)',
})

const InlineUpgradeLink = styled('button')({
  all: 'unset',
  cursor: 'pointer',
  fontSize: 11,
  fontWeight: 650,
  color: 'var(--ds-accent-dot-3, #be9dfd)',
  flexShrink: 0,
})

const QualityToggle = styled(Box)({
  display: 'flex',
  padding: 2,
  borderRadius: 9,
  background: 'var(--ds-hover, rgba(0,0,0,0.05))',
  flexShrink: 0,
})

const QualityBtn = styled('button', { shouldForwardProp: (p) => p !== '$on' })<{ $on?: boolean }>(({ $on }) => ({
  all: 'unset',
  boxSizing: 'border-box',
  cursor: 'pointer',
  padding: '5px 11px',
  borderRadius: 7,
  fontSize: 11,
  fontWeight: 650,
  color: $on ? 'var(--ds-inverse-ink, #fff)' : 'var(--ds-ink-subtle)',
  background: $on ? 'var(--ds-ink)' : 'transparent',
  transition: 'background var(--ds-duration-fast) var(--ds-ease), color var(--ds-duration-fast) var(--ds-ease)',
  '&:disabled': { opacity: 0.4, cursor: 'not-allowed' },
}))

function privacyMeta(privacy: AlbumPrivacy) {
  return privacy === 'private' ? 'PRIVATE' : 'KEY ACCESS'
}

export function CreateAlbumScreen() {
  const navigate = useNavigate()
  const isNarrow = useMediaQuery('(max-width:940px)')
  const { setStep } = useCreateAlbumLayout()
  const { create, isLoading: creating } = useCreateAlbum()
  const { uploadFiles, uploading } = useAlbumUpload(undefined)
  const { isFeatureAvailable, showUpgrade } = useFeatureGate()
  const fullResUnlocked = isFeatureAvailable(FEATURE_KEYS.FULL_RESOLUTION_UPLOAD)

  const [title, setTitle] = useState('')
  const [privacy, setPrivacy] = useState<AlbumPrivacy>('private')
  // Sets the album's initial topicXData.fullResolutionUploads; ownable later
  // from the Share sheet too (see heic-and-full-resolution-upload-spec.md §B7).
  const [uploadQuality, setUploadQuality] = useState<UploadQuality>('standard')
  // Sets the album's initial topicXData.allowDownload; ownable later from the
  // Share sheet too (see album-allow-download-spec.md). Default true to match
  // today's always-on behavior for existing albums.
  const [allowDownload, setAllowDownload] = useState(true)
  const [staged, setStaged] = useState<StagedFile[]>([])
  const [coverId, setCoverId] = useState<string | undefined>()
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  // Cache the topic once created so a retry after a failed upload re-attaches
  // photos instead of creating a second, duplicate album.
  const [createdTopicId, setCreatedTopicId] = useState<string | null>(null)

  const busy = creating || uploading || submitting
  const canSubmit = Boolean(title.trim() && staged.length > 0 && !busy)

  // Mirrors the mockup's two on-page states — NAME done on load, PHOTOS done
  // once photos are staged. The KEY segment only completes after navigating
  // into the created album, so it never lights up on this screen.
  useEffect(() => {
    setStep(staged.length > 0 ? 1 : 0)
  }, [staged.length, setStep])

  useEffect(() => {
    return () => {
      staged.forEach((s) => URL.revokeObjectURL(s.src))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- revoke only on unmount
  }, [])

  const queueItems: UploadQueueItem[] = useMemo(
    () => staged.map((s) => ({ id: s.id, src: s.src, progress: s.progress })),
    [staged],
  )

  const overallProgress = useMemo(() => {
    if (staged.length === 0) return 0
    return Math.round(staged.reduce((sum, s) => sum + s.progress, 0) / staged.length)
  }, [staged])

  const doneCount = staged.filter((s) => s.progress >= 100).length
  const statusLabel =
    submitting && doneCount < staged.length
      ? `Uploading ${staged.length - doneCount} of ${staged.length}…`
      : submitting && doneCount === staged.length
        ? 'All photos in ✓'
        : staged.length > 0
          ? `${staged.length} photo${staged.length === 1 ? '' : 's'} ready`
          : 'Uploading…'

  const coverSrc = staged.find((s) => s.id === coverId)?.src ?? staged[0]?.src
  const previewMeta = `${staged.length} PHOTOS · ${privacyMeta(privacy)}`

  const removeStaged = (id: string) => {
    setStaged((prev) => {
      const removed = prev.find((s) => s.id === id)
      if (removed) URL.revokeObjectURL(removed.src)
      const next = prev.filter((s) => s.id !== id)
      setCoverId((cur) => (cur === id ? next[0]?.id : cur))
      return next
    })
  }

  const onFilesSelected = (files: File[]) => {
    if (busy) return
    const next = files.map((file, i) => ({
      id: `${file.name}-${file.size}-${file.lastModified}-${i}-${Math.random().toString(36).slice(2, 7)}`,
      file,
      src: URL.createObjectURL(file),
      progress: 0,
    }))
    setStaged((prev) => {
      const merged = [...prev, ...next]
      if (!coverId && merged[0]) setCoverId(merged[0].id)
      return merged
    })
  }

  const onSubmit = async () => {
    if (!title.trim()) {
      setError('Give your album a name')
      return
    }
    if (staged.length === 0) {
      setError('Add at least one photo')
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      const wantsFullRes = uploadQuality === 'full' && fullResUnlocked
      const topicID =
        createdTopicId ??
        (await create({ title: title.trim(), privacy, fullResolutionUploads: wantsFullRes, allowDownload }))
      setCreatedTopicId(topicID)
      await uploadFiles(
        staged.map((s) => s.file),
        {
          albumId: topicID,
          silent: true,
          maxDim: wantsFullRes ? UPLOAD_RESIZE_DIM.full : UPLOAD_RESIZE_DIM.standard,
          onItemProgress: (index, progress) => {
            setStaged((prev) =>
              prev.map((item, i) => (i === index ? { ...item, progress } : item)),
            )
          },
        },
      )
      fireAlertToast('album.created')
      navigate(`/albums/${topicID}`, { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create album')
      setSubmitting(false)
    }
  }

  return (
    <Stage>
      <Panel>
        <Typography
          component='h1'
          sx={{
            fontSize: 28,
            fontWeight: 720,
            letterSpacing: '-0.035em',
            mb: '8px',
            color: 'var(--ds-ink)',
          }}
        >
          New album
        </Typography>
        <Typography
          sx={{
            fontSize: 14.5,
            color: 'var(--ds-ink-muted)',
            mb: '34px',
            lineHeight: 1.5,
          }}
        >
          A name, a privacy choice, your photos. The key comes free.
        </Typography>

        <FormGroup label='Name — you can change it anytime'>
          <Input
            variant='underline'
            autoFocus
            placeholder='Elena & Marcus'
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={busy}
            autoComplete='off'
          />
        </FormGroup>

        <FormGroup label='Who can open it'>
          <SegmentedOptionGroup
            options={PRIVACY_OPTIONS}
            value={privacy}
            onChange={(v) => setPrivacy(v as AlbumPrivacy)}
            sx={{
              flexDirection: isNarrow ? 'column' : 'row',
              pointerEvents: busy ? 'none' : 'auto',
              opacity: busy ? 0.6 : 1,
            }}
          />
        </FormGroup>

        <FormGroup>
          <SectionHeaderRow>
            <SectionHeaderLabel>Photos</SectionHeaderLabel>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ToggleLabel>Downloads</ToggleLabel>
                <QualityToggle role='radiogroup' aria-label='Allow guests to download originals'>
                  <QualityBtn
                    type='button'
                    role='radio'
                    aria-checked={allowDownload}
                    $on={allowDownload}
                    disabled={busy}
                    onClick={() => setAllowDownload(true)}
                  >
                    On
                  </QualityBtn>
                  <QualityBtn
                    type='button'
                    role='radio'
                    aria-checked={!allowDownload}
                    $on={!allowDownload}
                    disabled={busy}
                    onClick={() => setAllowDownload(false)}
                  >
                    Off
                  </QualityBtn>
                </QualityToggle>
              </Box>
              <QualityToggle role='radiogroup' aria-label='Upload quality'>
                <QualityBtn
                  type='button'
                  role='radio'
                  aria-checked={uploadQuality === 'standard'}
                  $on={uploadQuality === 'standard'}
                  disabled={busy}
                  onClick={() => setUploadQuality('standard')}
                >
                  Standard
                </QualityBtn>
                <QualityBtn
                  type='button'
                  role='radio'
                  aria-checked={uploadQuality === 'full'}
                  $on={uploadQuality === 'full'}
                  disabled={busy || !fullResUnlocked}
                  onClick={() => setUploadQuality('full')}
                >
                  ✨ Full
                </QualityBtn>
              </QualityToggle>
              {!fullResUnlocked && (
                <InlineUpgradeLink
                  type='button'
                  onClick={() => showUpgrade({ featureKey: FEATURE_KEYS.FULL_RESOLUTION_UPLOAD })}
                >
                  Gallery+ →
                </InlineUpgradeLink>
              )}
            </Box>
          </SectionHeaderRow>
          <Dropzone
            onFilesSelected={onFilesSelected}
            subtitle='HEIC, RAW, JPEG — batches welcome'
            hint={
              uploadQuality === 'full' && fullResUnlocked
                ? 'Full resolution · Up to 4000px'
                : 'Optimized for fast sharing · 1000px'
            }
            disabled={busy}
          />
          {staged.length > 0 && (
            <UploadQueue
              items={queueItems}
              statusLabel={statusLabel}
              percentLabel={`${overallProgress}%`}
              progress={overallProgress}
              onRemove={busy ? undefined : removeStaged}
              sx={{ mt: '18px' }}
            />
          )}
        </FormGroup>

        {error && (
          <Typography sx={{ color: 'var(--ds-danger)', fontSize: 14, mb: 2 }}>{error}</Typography>
        )}

        <GoRow>
          <Button
            variant='spectrum'
            size='lg'
            disabled={!canSubmit}
            loading={busy}
            onClick={onSubmit}
          >
            Open the album <Box component='span' sx={{ ml: 0.5 }}>→</Box>
          </Button>
          <Hint>
            {canSubmit
              ? '⚿ Key ready — under a minute, as promised'
              : 'Add a name and at least one photo'}
          </Hint>
        </GoRow>
      </Panel>

      <PreviewRail>
        <RailLabel>Live preview — how it appears in your library</RailLabel>
        <DraftPreviewCard
          title={title.trim() || 'Untitled album'}
          coverUrl={coverSrc}
          meta={previewMeta}
        />
        {staged.length > 0 && (
          <ThumbPicker
            items={staged.slice(0, 5).map((s) => ({ id: s.id, src: s.src }))}
            value={coverId ?? staged[0]?.id}
            onChange={setCoverId}
            sx={{ mt: '14px' }}
          />
        )}
        <Callout sx={{ mt: '18px' }}>
          ⚿ <b>Your key is ready the second you finish</b> — copy the link, or print the QR card for
          the table.
        </Callout>
      </PreviewRail>
    </Stage>
  )
}
